import { nextTick, onBeforeUnmount, ref, toValue, watch } from "vue";

function readWaveColors() {
  if (typeof document === "undefined") {
    return {
      waveColor: "rgba(15, 23, 42, 0.14)",
      progressColor: "#2563eb",
    };
  }
  const isDark = document.documentElement.classList.contains("dark");
  const root = document.documentElement;
  const progressColor =
    getComputedStyle(root).getPropertyValue("--color-primary-500").trim() ||
    getComputedStyle(root).getPropertyValue("--color-accent-600").trim() ||
    "#2563eb";
  const waveColor = isDark ? "rgba(255,255,255,0.22)" : "rgba(15, 23, 42, 0.14)";
  return { waveColor, progressColor };
}

function resolveHeightOption(options) {
  return toValue(options.height) ?? 20;
}

/**
 * WaveSurfer.js player for compact history / creation audio bars.
 * @param {import('vue').MaybeRefOrGetter<string>} srcSource
 * @param {import('vue').MaybeRefOrGetter<HTMLElement|null>} containerSource
 * @param {{ height?: import('vue').MaybeRefOrGetter<number> }} [options]
 */
export function useHistoryAudioWaveform(srcSource, containerSource, options = {}) {
  const loading = ref(false);
  const playing = ref(false);
  const currentTime = ref(0);
  const duration = ref(0);
  const waveformReady = ref(false);

  let wavesurfer = null;
  let objectUrl = "";
  let initGen = 0;

  function revokeObjectUrl() {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = "";
    }
  }

  function destroyWaveform() {
    if (wavesurfer) {
      wavesurfer.destroy();
      wavesurfer = null;
    }
    revokeObjectUrl();
    playing.value = false;
    currentTime.value = 0;
    duration.value = 0;
    waveformReady.value = false;
  }

  async function loadWaveformSource(ws, src, gen) {
    try {
      await ws.load(src);
      return;
    } catch {
      /* direct URL failed — retry via blob (CORS / decode) */
    }

    const res = await fetch(src, { mode: "cors" });
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    if (gen !== initGen) throw new Error("stale");
    revokeObjectUrl();
    objectUrl = URL.createObjectURL(blob);
    await ws.load(objectUrl);
  }

  async function initWaveform() {
    const src = String(toValue(srcSource) || "").trim();
    await nextTick();
    const container = toValue(containerSource);

    if (!src || !container || typeof window === "undefined") {
      destroyWaveform();
      loading.value = false;
      return;
    }

    const gen = ++initGen;
    loading.value = true;
    waveformReady.value = false;
    destroyWaveform();
    initGen = gen;

    try {
      const WaveSurfer = (await import("wavesurfer.js")).default;
      await nextTick();
      const mountEl = toValue(containerSource);
      if (gen !== initGen || !mountEl) {
        if (gen === initGen) loading.value = false;
        return;
      }

      mountEl.innerHTML = "";
      const { waveColor, progressColor } = readWaveColors();
      wavesurfer = WaveSurfer.create({
        container: mountEl,
        waveColor,
        progressColor,
        cursorWidth: 0,
        barWidth: 2,
        barGap: 2,
        height: resolveHeightOption(options),
        normalize: true,
        interact: false,
      });

      wavesurfer.on("timeupdate", (time) => {
        if (gen !== initGen) return;
        currentTime.value = time;
      });

      wavesurfer.on("play", () => {
        if (gen !== initGen) return;
        playing.value = true;
      });

      wavesurfer.on("pause", () => {
        if (gen !== initGen) return;
        playing.value = false;
      });

      wavesurfer.on("finish", () => {
        if (gen !== initGen) return;
        playing.value = false;
        currentTime.value = 0;
        wavesurfer.seekTo(0);
      });

      await loadWaveformSource(wavesurfer, src, gen);
      if (gen !== initGen || !wavesurfer) {
        if (gen === initGen) loading.value = false;
        return;
      }

      duration.value = wavesurfer.getDuration() || 0;
      loading.value = false;
      waveformReady.value = true;
    } catch {
      if (gen === initGen) {
        loading.value = false;
        waveformReady.value = false;
      }
    }
  }

  function togglePlayback() {
    if (wavesurfer && waveformReady.value) {
      wavesurfer.playPause();
      return false;
    }
    return true;
  }

  watch(
    () => [toValue(srcSource), toValue(containerSource), resolveHeightOption(options)],
    () => {
      initWaveform();
    },
    { flush: "post" },
  );

  onBeforeUnmount(() => {
    initGen += 1;
    destroyWaveform();
  });

  return {
    loading,
    playing,
    currentTime,
    duration,
    waveformReady,
    togglePlayback,
    initWaveform,
    destroyWaveform,
  };
}
